import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Box as MuiBox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function ScanActivityView() {
  const { token } = useAuth();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', type: 'info' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
    planogramId: '',
    location: ''
  });
  
  const [users, setUsers] = useState([]);
  const [planograms, setPlanograms] = useState([]);
  const [stats, setStats] = useState({
    totalScans: 0,
    uniqueBinCount: 0,
    uniqueUserCount: 0,
    uniquePlanogramCount: 0
  });
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearTarget, setClearTarget] = useState({ type: '', id: '', name: '' });

  // Load users and planograms for filters
  const loadFilterData = async () => {
    try {
      // Load users
      const usersResponse = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData);
      }

      // Load planograms
      const planogramsResponse = await fetch(`${config.apiUrl}/planograms/list/dropdown`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (planogramsResponse.ok) {
        const planogramsData = await planogramsResponse.json();
        setPlanograms(planogramsData);
      }
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  };

  // Load scans
  const loadScans = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      // Add filters to query
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`${config.apiUrl}/scans/global?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setScans(data.scans);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      } else {
        setStatus({ message: 'Failed to load scan data', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: 'Error loading scans: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`${config.apiUrl}/scans/stats?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadFilterData();
  }, []);

  useEffect(() => {
    loadScans();
    loadStats();
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportScans = () => {
    const csvContent = [
      ['Date', 'User', 'Planogram', 'Location', 'Bin ID'],
      ...scans.map(scan => [
        new Date(scan.scannedAt).toLocaleString(),
        `${scan.scannedBy?.firstName || ''} ${scan.scannedBy?.lastName || ''}`.trim() || scan.scannedBy?.username || 'Unknown',
        scan.planogramName || scan.planogramId?.name || 'Unknown',
        scan.location || 'Unknown',
        scan.binId
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `scan_activity_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportMissingBins = async () => {
    try {
      // Get all planograms to find missing bins
      const planogramsResponse = await fetch(`${config.apiUrl}/planograms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!planogramsResponse.ok) return;
      
      const planograms = await planogramsResponse.json();
      const missingBins = [];
      
      for (const planogram of planograms) {
        // Get all scans for this planogram
        const scansResponse = await fetch(`${config.apiUrl}/scans/planogram/${planogram._id}/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!scansResponse.ok) continue;
        
        const scans = await scansResponse.json();
        const scannedBinIds = new Set(scans.map(scan => scan.binId));
        
        // Find missing bins
        const missing = planogram.bins.filter(bin => !scannedBinIds.has(bin.slnPositionId));
        missingBins.push(...missing.map(bin => ({
          planogram: planogram.name,
          customer: planogram.customer,
          location: planogram.location,
          binId: bin.slnPositionId,
          fastenalPart: bin.fastenalPart,
          customerPart: bin.customerPart,
          description: bin.skuDescription
        })));
      }
      
      if (missingBins.length === 0) {
        setStatus({ message: 'No missing bins found!', type: 'success' });
        return;
      }
      
      const csvContent = [
        ['Planogram', 'Customer', 'Location', 'Bin ID', 'Fastenal Part #', 'Customer Part #', 'Description'],
        ...missingBins.map(bin => [
          bin.planogram,
          bin.customer,
          bin.location,
          bin.binId,
          bin.fastenalPart,
          bin.customerPart,
          bin.description
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `missing_bins_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      
      setStatus({ message: `Exported ${missingBins.length} missing bins`, type: 'success' });
    } catch (error) {
      setStatus({ message: `Error exporting missing bins: ${error.message}`, type: 'error' });
    }
  };

  const handleClearScans = (type, id, name) => {
    setClearTarget({ type, id, name });
    setClearDialogOpen(true);
  };

  const confirmClearScans = async () => {
    try {
      let response;
      if (clearTarget.type === 'planogram') {
        response = await fetch(`${config.apiUrl}/scans/planogram/${clearTarget.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else if (clearTarget.type === 'user') {
        response = await fetch(`${config.apiUrl}/scans/my-scans`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userId: clearTarget.id })
        });
      } else if (clearTarget.type === 'all') {
        response = await fetch(`${config.apiUrl}/scans/clear-all`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      if (response && response.ok) {
        setStatus({ message: `Cleared scans for ${clearTarget.name}`, type: 'success' });
        loadScans();
        loadStats();
      } else {
        setStatus({ message: 'Failed to clear scans', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: `Error clearing scans: ${error.message}`, type: 'error' });
    } finally {
      setClearDialogOpen(false);
      setClearTarget({ type: '', id: '', name: '' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Global Scan Activity
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadScans}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportScans}
            disabled={scans.length === 0}
          >
            Export Scans
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportMissingBins}
          >
            Export Missing Bins
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleClearScans('all', '', 'All Scans')}
            disabled={scans.length === 0}
          >
            Clear All Scans
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and analyze scanning activity across all users and planograms.
      </Typography>

      {status.message && (
        <Alert severity={status.type} sx={{ mb: 3 }}>
          {status.message}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Scans
              </Typography>
              <Typography variant="h4">
                {stats.totalScans.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Unique Bins
              </Typography>
              <Typography variant="h4">
                {stats.uniqueBinCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h4">
                {stats.uniqueUserCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Planograms
              </Typography>
              <Typography variant="h4">
                {stats.uniquePlanogramCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title="Filters" 
          avatar={<FilterIcon />}
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  label="User"
                >
                  <MenuItem value="">All Users</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Planogram</InputLabel>
                <Select
                  value={filters.planogramId}
                  onChange={(e) => handleFilterChange('planogramId', e.target.value)}
                  label="Planogram"
                >
                  <MenuItem value="">All Planograms</MenuItem>
                  {planograms.map(planogram => (
                    <MenuItem key={planogram.id} value={planogram.id}>
                      {planogram.name} ({planogram.customer} - {planogram.location})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Location"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="Filter by location..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Scans Table */}
      <Card>
        <CardHeader title="Scan Activity" />
        <CardContent>
          {loading ? (
            <MuiBox sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </MuiBox>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date & Time</strong></TableCell>
                      <TableCell><strong>User</strong></TableCell>
                      <TableCell><strong>Planogram</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Bin ID</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scans.map((scan, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          {new Date(scan.scannedAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {scan.scannedBy?.firstName} {scan.scannedBy?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{scan.scannedBy?.username}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scan.planogramName || scan.planogramId?.name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={scan.location || 'Unknown'} size="small" />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {scan.binId}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Clear Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Clear Scans</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear all scans for <strong>{clearTarget.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmClearScans} 
            color="error" 
            variant="contained"
          >
            Clear Scans
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ScanActivityView;
